import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Shield, Trash2, X, Mail, Lock, History, AlertTriangle, ShieldCheck, ShieldOff, Unlock, Bell } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { PasswordInput } from "../components/shared/PasswordInput";
import { CountryFlag } from "../components/shared/CountryFlag";

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'it', label: 'IT Manager' },
  { value: 'salesforce', label: 'Salesforce Admin' },
  { value: 'viewer', label: 'Viewer' },
];

// Keep aligned with backend `services/tech_alert_preferences.py::CATEGORIES`.
// Order matters — displayed as-is in the edit form.
const TECH_ALERT_CATEGORIES = [
  { key: 'wallet',  label: 'Wallet',        hint: 'APN cert, Google Wallet, scadenza .p12' },
  { key: 'crm',     label: 'CRM',           hint: 'Salesforce webhook / connessione' },
  { key: 'ai',      label: 'AI Translation', hint: 'Backend LLM (traduzioni PDF, helper admin)' },
  { key: 'storage', label: 'Backup Cloud',  hint: 'Server dedicati TelcaVoIP — Regione Europa' },
  { key: 'infra',   label: 'Infrastruttura', hint: 'MongoDB, LibreOffice, container health' },
];

// Defaults applied on the frontend when a user record predates the
// migration and doesn't yet carry a `tech_alert_preferences` map.
// Kept aligned with `default_preferences_for_role` on the backend.
const roleDefaultPrefs = (role) => {
  const allOn = role === 'super_admin' || role === 'it';
  return Object.fromEntries(TECH_ALERT_CATEGORIES.map(c => [c.key, allOn]));
};

// Country options for the "preferred dashboard filter" dropdown on
// admin user create/edit. Mirrors the backend whitelist in
// ``services/i18n.py::ALLOWED_PREFERRED_COUNTRIES`` AND the frontend
// dropdown options in ``components/shared/CountryFilterDropdown.jsx``.
// Empty value (``""``) = no preference (admin sees all countries).
const COUNTRY_OPTIONS = [
  { value: '',    label: '— Tutti i paesi (nessuna preferenza) —' },
  { value: 'IT',  label: 'IT  —  Italia' },
  { value: 'FR',  label: 'FR  —  France' },
  { value: 'ES',  label: 'ES  —  España' },
  { value: 'DE',  label: 'DE  —  Deutschland' },
  { value: 'NL',  label: 'NL  —  Nederland' },
  { value: 'BE',  label: 'BE  —  Belgique / België' },
  { value: 'PT',  label: 'PT  —  Portugal' },
  { value: 'CH',  label: 'CH  —  Schweiz' },
  { value: 'PL',  label: 'PL  —  Polska' },
  { value: 'CZ',  label: 'CZ  —  Česká republika' },
  { value: 'SE',  label: 'SE  —  Sverige' },
  { value: 'NO',  label: 'NO  —  Norge' },
  { value: 'FI',  label: 'FI  —  Suomi' },
  { value: 'DK',  label: 'DK  —  Danmark' },
  { value: 'GR',  label: 'GR  —  Elláda' },
  { value: 'UK',  label: 'UK  —  United Kingdom' },
  { value: 'IRL', label: 'IRL —  Ireland' },
  { value: 'GBZ', label: 'GBZ —  Gibraltar' },
];

const roleColors = {
  super_admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  viewer: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  it: 'bg-lojack-blue-500/20 text-lojack-blue-400 border-lojack-blue-500/30',
  salesforce: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};
const roleLabels = {
  super_admin: 'Super Admin', admin: 'Admin', viewer: 'Viewer', it: 'IT Manager', salesforce: 'Salesforce Admin',
};

const EditUserForm = ({ user, onSave, onClose }) => {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.is_active !== false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [preferredCountry, setPreferredCountry] = useState(user.preferred_country || '');
  const [techPrefs, setTechPrefs] = useState(() => {
    const raw = user.tech_alert_preferences;
    if (raw && typeof raw === 'object' && Object.keys(raw).length > 0) return { ...raw };
    return roleDefaultPrefs(user.role);
  });
  const [saving, setSaving] = useState(false);

  const passwordRequested = password.length > 0;
  const passwordMismatch = passwordRequested && password !== passwordConfirm;

  const currentPrefs = (user.tech_alert_preferences && Object.keys(user.tech_alert_preferences).length > 0)
    ? user.tech_alert_preferences
    : roleDefaultPrefs(user.role);
  const prefsChanged = TECH_ALERT_CATEGORIES.some(c => !!techPrefs[c.key] !== !!currentPrefs[c.key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwordMismatch) { toast.error("Le password non coincidono"); return; }
    if (passwordRequested && !showPasswordConfirm) { setShowPasswordConfirm(true); return; }
    setSaving(true);
    const updates = {};
    if (name !== user.name) updates.name = name;
    if (role !== user.role) updates.role = role;
    if (isActive !== (user.is_active !== false)) updates.is_active = isActive;
    if (password) updates.password = password;
    // ``preferred_country``: normalize empty string to null (= reset).
    // Always send the field when the form value differs from current
    // user; the backend uses Pydantic's ``model_fields_set`` to
    // distinguish "explicit clear" from "field omitted".
    const newCountry = preferredCountry || null;
    const currentCountry = user.preferred_country || null;
    if (newCountry !== currentCountry) updates.preferred_country = newCountry;
    if (prefsChanged) updates.tech_alert_preferences = techPrefs;
    if (Object.keys(updates).length === 0) { onClose(); return; }
    await onSave(updates);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="p-5 space-y-4">
      <div>
        <label className="text-gray-400 text-xs">Nome</label>
        <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="edit-user-name" />
      </div>
      <div>
        <label className="text-gray-400 text-xs">Email</label>
        <input value={user.email} disabled className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-gray-500" />
      </div>
      <div>
        <label className="text-gray-400 text-xs">Ruolo</label>
        <select value={role} onChange={e => setRole(e.target.value)} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="edit-user-role">
          {ROLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value} style={{backgroundColor:'#111'}}>{opt.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-gray-400 text-xs flex items-center gap-2">
          <Shield size={12} className={isActive ? 'text-lojack-blue-400' : 'text-red-400'} />
          Stato Account
        </label>
        <div className="flex gap-2 mt-1">
          <button type="button" onClick={() => setIsActive(true)} className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${isActive ? 'bg-lojack-blue-500/20 text-lojack-blue-400 border-lojack-blue-500/40' : 'bg-[#111] text-gray-500 border-gray-700 hover:border-gray-600'}`} data-testid="edit-user-active">Attivo</button>
          <button type="button" onClick={() => setIsActive(false)} className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${!isActive ? 'bg-red-500/20 text-red-400 border-red-500/40' : 'bg-[#111] text-gray-500 border-gray-700 hover:border-gray-600'}`} data-testid="edit-user-inactive">Disattivato</button>
        </div>
      </div>
      <div>
        <label className="text-gray-400 text-xs">
          Paese di lavoro (filtro dashboard di default)
        </label>
        <select
          value={preferredCountry}
          onChange={e => setPreferredCountry(e.target.value)}
          className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
          data-testid="edit-user-preferred-country"
        >
          {COUNTRY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value} style={{ backgroundColor: '#111' }}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-gray-600 text-[10px] mt-1">
          Se impostato, al login l'admin troverà la dashboard già filtrata per questo paese.
        </p>
      </div>
      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-lojack-blue-400" />
          <label className="text-gray-300 text-xs font-medium">Notifiche tecniche per categoria</label>
        </div>
        <p className="text-gray-600 text-[10px] leading-relaxed">
          Ogni utente può ricevere alert email quando una specifica categoria entra in warning/errore.
          L'utente può modificare in autonomia queste preferenze dal proprio pannello. Come super admin puoi comunque forzare qui la scelta.
        </p>
        <div className="space-y-2">
          {TECH_ALERT_CATEGORIES.map(cat => {
            const isOn = !!techPrefs[cat.key];
            return (
              <div
                key={cat.key}
                className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
                  isOn ? 'bg-lojack-blue-500/5 border-lojack-blue-500/30' : 'bg-[#111] border-gray-800'
                }`}
                data-testid={`edit-user-tech-alert-row-${cat.key}`}
              >
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium ${isOn ? 'text-white' : 'text-gray-300'}`}>{cat.label}</p>
                  <p className="text-gray-500 text-[10px] mt-0.5">{cat.hint}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setTechPrefs({ ...techPrefs, [cat.key]: !isOn })}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    isOn ? 'bg-lojack-blue-500' : 'bg-gray-700'
                  }`}
                  role="switch"
                  aria-checked={isOn}
                  data-testid={`edit-user-tech-alert-toggle-${cat.key}`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                      isOn ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-gray-400 text-xs flex items-center gap-2"><Lock size={12} />Nuova Password (lascia vuoto per non cambiare)</label>
        <PasswordInput value={password} onChange={e => { setPassword(e.target.value); setShowPasswordConfirm(false); }} className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" placeholder="..." data-testid="edit-user-password" />
      </div>
      {passwordRequested && (
        <div>
          <label className="text-gray-400 text-xs">Conferma Password</label>
          <PasswordInput value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} className={`w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border rounded-xl text-white focus:outline-none ${passwordMismatch ? 'border-red-500' : 'border-gray-700 focus:border-lojack-red'}`} placeholder="Ripeti la password" data-testid="edit-user-password-confirm" />
          {passwordMismatch && <p className="text-red-400 text-xs mt-1">Le password non coincidono</p>}
        </div>
      )}
      {passwordRequested && showPasswordConfirm && !passwordMismatch && (
        <div className="bg-lojack-clause-500/10 border border-lojack-clause-500/40 rounded-xl p-3" data-testid="password-change-warning">
          <p className="text-lojack-clause-300 text-xs flex items-start gap-2"><AlertTriangle size={14} className="shrink-0 mt-0.5" /><span><strong>Attenzione:</strong> stai cambiando la password di <strong>{user.email}</strong>. L'utente riceverà una notifica email automatica. L'operazione sarà loggata nell'audit di sicurezza. Clicca di nuovo &quot;Salva&quot; per confermare.</span></p>
        </div>
      )}
      <button type="submit" disabled={saving || passwordMismatch} className="btn-lojack w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="edit-user-submit">
        {saving ? 'Salvataggio...' : (passwordRequested && !showPasswordConfirm ? 'Continua' : 'Salva Modifiche')}
      </button>
    </form>
  );
};

const ACTION_LABELS = {
  'user.create': 'Creazione utente',
  'user.update': 'Modifica utente',
  'user.delete': 'Eliminazione utente',
  'user.password_reset_email': 'Reset password via email',
  'security.cross_owner_attempt_blocked': '🚨 Tentativo bloccato su Owner',
  'security.actor_auto_locked': '🔒 Account auto-bloccato',
  'security.actor_unlocked': '🔓 Account sbloccato manualmente',
  'auth.login_failed': '🚪 Login fallito',
  'auth.lockout_account': '🚪 Lockout account (login)',
  'auth.lockout_ip': '🚪 Lockout IP (scanner)',
  'auth.lockout_owner_probe': '🚨 Brute-force su Owner',
};

const OPERATION_LABELS = {
  password_change: 'reset password',
  role_change: 'cambio ruolo',
  deactivate: 'disattivazione',
  delete: 'eliminazione',
  modify: 'modifica',
};

const AuditLogPanel = ({ onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'security' | 'user' | 'auth'
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      const prefix = filter === 'security' ? 'security.' : filter === 'user' ? 'user.' : filter === 'auth' ? 'auth.' : '';
      const url = prefix ? `${API}/admin/audit?action_prefix=${prefix}&limit=100` : `${API}/admin/audit?limit=100`;
      try {
        const res = await axios.get(url, getAuthHeader());
        if (mounted) setEntries(res.data.entries || []);
      } catch {
        if (mounted) toast.error('Errore caricamento audit log');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
    } catch { return iso; }
  };

  const isSecurity = (a) => a?.startsWith('security.') || a?.startsWith('auth.lockout_') || a === 'auth.login_failed';

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={onClose}>
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-3xl border border-gray-700 overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="audit-log-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <History size={20} className="text-lojack-clause-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Audit Log Utenti</h2>
              <p className="text-gray-500 text-xs">Cronologia modifiche e tentativi di sicurezza</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="px-5 pt-4 pb-3 border-b border-gray-800 flex items-center gap-2">
          {[
            { key:'all', label:'Tutto' },
            { key:'security', label:'🚨 Sicurezza' },
            { key:'auth', label:'🚪 Login' },
            { key:'user', label:'Modifiche utenti' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.key ? 'bg-lojack-red text-white' : 'bg-[#111] text-gray-400 hover:text-white border border-gray-700'}`} data-testid={`audit-filter-${f.key}`}>{f.label}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner"></div></div>
          ) : entries.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">Nessuna voce di audit log</p>
          ) : (
            <div className="space-y-2" data-testid="audit-log-entries">
              {entries.map((e, idx) => {
                const sec = isSecurity(e.action);
                const op = e.metadata?.operation;
                return (
                  <div key={idx} className={`border rounded-lg p-3 text-sm ${sec ? 'bg-red-950/30 border-red-700/60' : 'bg-[#111] border-gray-800'}`} data-testid={`audit-entry-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${sec ? 'text-red-300' : 'text-white'}`}>{ACTION_LABELS[e.action] || e.action}</p>
                        <p className="text-gray-400 text-xs mt-1">
                          <span className="text-gray-500">Attore:</span> {e.actor_email}{' '}
                          {e.target_email && (<><span className="text-gray-500">→ Target:</span> {e.target_email}</>)}
                        </p>
                        {sec && (
                          <div className="mt-2 space-y-1">
                            {op && <p className="text-xs text-red-300 font-mono"><span className="text-gray-500">Operazione tentata:</span> {OPERATION_LABELS[op] || op}</p>}
                            {e.metadata?.client_ip && <p className="text-xs text-gray-400 font-mono"><span className="text-gray-500">IP:</span> {e.metadata.client_ip}</p>}
                            {e.metadata?.user_agent && <p className="text-xs text-gray-500 font-mono break-all line-clamp-2"><span className="text-gray-500">UA:</span> {e.metadata.user_agent}</p>}
                          </div>
                        )}
                        {!sec && e.metadata?.changes && (
                          <div className="mt-2 space-y-1">
                            {Object.entries(e.metadata.changes).map(([field, ch]) => (
                              <p key={field} className="text-xs text-gray-400 font-mono">
                                <span className="text-lojack-clause-400">{field}:</span>{' '}
                                {ch.changed ? <span className="text-red-400">(password modificata)</span> : (
                                  <><span className="text-red-400 line-through">{String(ch.before)}</span>{' → '}<span className="text-lojack-blue-400">{String(ch.after)}</span></>
                                )}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-gray-500 text-xs whitespace-nowrap">{fmt(e.at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin', preferred_country: '' });
  const [creating, setCreating] = useState(false);
  const { getAuthHeader, user: currentUser } = useAuth();

  const fetchUsers = async () => { setLoading(true); try { const res = await axios.get(`${API}/admin/users`, getAuthHeader()); setUsers(res.data); } catch { toast.error('Errore caricamento utenti'); } finally { setLoading(false); } };
  useEffect(() => { fetchUsers(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      // Normalize empty preferred_country to null so the backend
      // doesn't try to whitelist-validate an empty string.
      const payload = { ...form, preferred_country: form.preferred_country || null };
      await axios.post(`${API}/admin/users`, payload, getAuthHeader());
      toast.success('Utente creato con successo');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'admin', preferred_country: '' });
      fetchUsers();
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore creazione utente'); } finally { setCreating(false); }
  };

  const handleUpdate = async (userId, updates) => { try { await axios.put(`${API}/admin/users/${userId}`, updates, getAuthHeader()); toast.success('Utente aggiornato'); setEditUser(null); fetchUsers(); } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } };
  const handleDelete = async (u) => { if (!window.confirm(`Eliminare l'utente ${u.name}?\n\nL'utente riceverà una notifica email e l'operazione sarà loggata nell'audit.`)) return; try { await axios.delete(`${API}/admin/users/${u.id}`, getAuthHeader()); toast.success('Utente eliminato'); fetchUsers(); } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } };
  const resendITEmail = async (u) => { try { const res = await axios.post(`${API}/admin/users/${u.id}/resend-email`, {}, getAuthHeader()); toast.success(res.data.message); } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio email'); } };
  const handleUnlock = async (u) => {
    if (!window.confirm(`Sbloccare l'account ${u.name} (${u.email})?\n\nL'utente è stato bloccato automaticamente dopo ${u.lockout_trigger_count || '3+'} tentativi cross-owner. Verifica di aver chiarito l'incidente prima di sbloccare.`)) return;
    try { await axios.post(`${API}/admin/users/${u.id}/unlock`, {}, getAuthHeader()); toast.success(`Account ${u.email} sbloccato`); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // A user is "protected" from MY actions if it's a platform owner AND it's
  // not me. Owners can still edit their own account. The backend enforces
  // this regardless of UI state.
  const isProtectedFromMe = (u) => u.is_platform_owner && u.email !== currentUser?.email;

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-3xl font-bold text-white">Gestione Utenti</h1><p className="text-gray-400 text-sm mt-1">{users.length} utenti registrati</p></div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAuditLog(true)} className="px-4 py-2 bg-[#1A1A1A] border border-gray-700 hover:border-lojack-clause-500/50 hover:bg-lojack-clause-500/10 text-gray-300 hover:text-lojack-clause-400 rounded-xl flex items-center gap-2 transition-colors" data-testid="open-audit-log-btn"><History size={16} /> Audit Log</button>
          <button onClick={() => setShowAdd(true)} className="btn-lojack flex items-center gap-2" data-testid="add-user-btn"><Plus size={18} /> Nuovo Utente</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.id} className="wallet-card flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${u.is_active ? 'bg-lojack-red/20 text-lojack-red' : 'bg-red-500/20 text-red-400'}`}>{u.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}</div>
                <div>
                  <p className="text-white font-medium flex items-center gap-2">
                    {u.name}
                    {u.preferred_country && (
                      <CountryFlag
                        code={u.preferred_country}
                        title={`Referente ${u.preferred_country}`}
                        className="ml-0.5"
                      />
                    )}
                    {u.is_platform_owner && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-lojack-clause-500/15 text-lojack-clause-400 border border-lojack-clause-500/30" title="Platform Owner — account protetto da modifiche esterne" data-testid={`owner-badge-${u.id}`}>
                        <ShieldCheck size={10} /> Owner
                      </span>
                    )}
                    {u.auto_locked && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/15 text-red-400 border border-red-500/40 animate-pulse" title={`Bloccato automaticamente dal sistema di sicurezza (${u.lockout_trigger_count || '3+'} tentativi cross-owner)`} data-testid={`autolock-badge-${u.id}`}>
                        <ShieldOff size={10} /> Auto-bloccato
                      </span>
                    )}
                  </p>
                  <p className="text-gray-500 text-xs">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${roleColors[u.role] || roleColors.viewer}`} data-testid={`user-role-${u.id}`}>{roleLabels[u.role] || u.role}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${u.is_active ? 'bg-lojack-blue-500/20 text-lojack-blue-400' : 'bg-red-500/20 text-red-400'}`}>{u.is_active ? 'Attivo' : 'Disattivato'}</span>
                {u.email === currentUser?.email ? (
                  <span className="text-gray-600 text-xs italic">Tu</span>
                ) : u.auto_locked ? (
                  <button onClick={() => handleUnlock(u)} className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-colors" title="Sblocca account auto-bloccato dal honeypot" data-testid={`unlock-user-${u.id}`}>
                    <Unlock size={12} /> Sblocca
                  </button>
                ) : isProtectedFromMe(u) ? (
                  <span className="text-lojack-clause-500/70 text-xs italic flex items-center gap-1" title="Account Owner protetto — modifica disponibile solo all'intestatario"><Lock size={12} /> Protetto</span>
                ) : (
                  <>
                    {u.role === 'it' && <button onClick={() => resendITEmail(u)} className="text-gray-500 hover:text-lojack-blue-400 transition-colors" title="Invia credenziali IT via email" data-testid={`resend-it-${u.id}`}><Mail size={16} /></button>}
                    <button onClick={() => setEditUser(u)} className="text-gray-500 hover:text-blue-400 transition-colors" title="Modifica utente" data-testid={`edit-user-${u.id}`}><Pencil size={16} /></button>
                    <button onClick={() => handleUpdate(u.id, { is_active: !u.is_active })} className="text-gray-500 hover:text-lojack-clause-400 transition-colors" title={u.is_active ? 'Disattiva' : 'Riattiva'} data-testid={`toggle-user-${u.id}`}><Shield size={16} /></button>
                    <button onClick={() => handleDelete(u)} className="text-gray-500 hover:text-red-400 transition-colors" title="Elimina" data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={() => setShowAdd(false)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700"><h2 className="text-lg font-bold text-white" data-testid="add-user-title">Nuovo Utente</h2><button onClick={() => setShowAdd(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button></div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div><label className="text-gray-400 text-xs">Nome Completo*</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="user-form-name" /></div>
              <div><label className="text-gray-400 text-xs">Email*</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="user-form-email" /></div>
              <div><label className="text-gray-400 text-xs">Password*</label><PasswordInput value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="user-form-password" /></div>
              <div><label className="text-gray-400 text-xs">Ruolo*</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="user-form-role">
                  {ROLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value} style={{backgroundColor:'#111'}}>{opt.label}</option>)}
                </select>
                <p className="text-gray-600 text-[10px] mt-1.5">Super Admin: accesso completo | Admin: gestisce clienti e dealer | IT Manager: portale IT dedicato | Salesforce Admin: console CRM | Viewer: sola lettura</p></div>
              <div>
                <label className="text-gray-400 text-xs">Paese di lavoro (filtro dashboard di default)</label>
                <select
                  value={form.preferred_country}
                  onChange={e => setForm({ ...form, preferred_country: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                  data-testid="user-form-preferred-country"
                >
                  {COUNTRY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} style={{ backgroundColor: '#111' }}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-gray-600 text-[10px] mt-1.5">
                  Se impostato, al primo login il nuovo admin troverà la dashboard già filtrata per questo paese. Può sempre cambiarlo dal dropdown country in alto.
                </p>
              </div>
              <button type="submit" disabled={creating} className="btn-lojack w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="user-form-submit">{creating ? 'Creazione...' : 'Crea Utente'}</button>
            </form>
          </div>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={() => setEditUser(null)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700"><h2 className="text-lg font-bold text-white">Modifica Utente</h2><button onClick={() => setEditUser(null)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button></div>
            <EditUserForm user={editUser} onSave={(updates) => handleUpdate(editUser.id, updates)} onClose={() => setEditUser(null)} />
          </div>
        </div>
      )}

      {showAuditLog && <AuditLogPanel onClose={() => setShowAuditLog(false)} />}
    </div>
  );
};

export default AdminUsersPage;
